// Date-only strings (YYYY-MM-DD) are parsed as UTC midnight by spec — force local interpretation.
const normalizeToLocalDate = (date: Date | string): Date => {
  const d = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
    ? new Date(`${date}T00:00:00`)
    : new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const formatDateToString = (date: Date | null): string => {
  return date ? date.toISOString().split('T')[0] : '';
};

const parseDateFromString = (dateString: string): Date | null => {
  return dateString ? new Date(dateString) : null;
};

/**
 * Format a date string to a human-readable format
 * @param dateString - ISO date string
 * @returns Formatted date string (e.g., "August 22, 2025")
 */
const formatDateForDisplay = (dateString: string): string => {
  return normalizeToLocalDate(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Format a date string to a short DD-MMM-YYYY format (e.g., "13-Aug-2023").
 * Returns empty string for falsy/invalid input.
 */
const formatDateShort = (dateString: string | null): string => {
  if (!dateString) return '';
  const d = normalizeToLocalDate(dateString);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Format a date-time string to short DD-MMM-YYYY, HH:mm format (e.g., "08-Jul-2023, 10:30").
 * Returns empty string for falsy/invalid input.
 */
const formatDateShortWithTime = (dateTimeString: string): string => {
  if (!dateTimeString) return '';
  const d = new Date(dateTimeString);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year}, ${hours}:${minutes}`;
};

export {
  formatDateForDisplay,
  formatDateShort,
  formatDateShortWithTime,
  formatDateToString,
  normalizeToLocalDate,
  parseDateFromString,
};
